import { ThemeProvider } from '@emotion/react';
import { Container, createTheme, Toolbar } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser';
import './App.css';
import NavigationBar from './lib/components/NavigationBar';
import { AuthenticatedTemplate, useIsAuthenticated, useMsal } from '@azure/msal-react'
import { getEnvironment, getUser } from './lib/services/userService';
import { loginRequest } from './lib/services/authConfig';
import NavigationDrawer from './lib/components/NavigationDrawer';
import HomePage from './app/HomePage/HomePage';
import ExplorePage from './app/ExplorePage/App';
import fetchGlobalSettings from './lib/services/globalSettings';
import SecurityRoute from './lib/components/SecurityRoute';
import CustomViewPage from './app/CustomViewPage/CustomViewPage';
import UserInteractionPage from './app/UserInteractionPage/UserInteractionPage';
import DataProductResult from './app/ExplorePage/DataProductResult';
import DataProduct from './app/ExplorePage/DataProduct';
import UnauthorizedPage from './app/UnauthorizedPage/UnauthorizedPage';
import AdminPage from './app/AdminPage/AdminPage';
import CustomReportsPage from './app/ReportsPage/CustomReportsPage';
import ManageLists from './app/ManageListsPage/ManageLists';
import EditList from './lib/components/EditList';
import ManageQueries from './lib/components/ManageQueries';
import EditQuery from './lib/components/EditQuery';
import ExternalReportsPage from './app/ExternalReportsPage/ExternalReportsPage';
import SnowflakeDataSourcesPage from './app/SnowflakeDataSourcesPage/SnowflakeDataSourcesPage';
import ApiHubPage from './app/ApiHubPage/ApiHubPage';
import ApiHubProductPage from './app/ApiHubPage/ApiHubProductPage';
import FinalMileAdminPage from './app/FinalMileAdminPage/FinalMileAdminPage';
import FinalMileAdminProductPage from './app/FinalMileAdminPage/FinalMileAdminProductPage';
import { fetchFmApiHubAccess } from './lib/services/fmDataService';

// Standard theme for standalone app
const standardTheme = createTheme({
    palette: {
        primary: {
            main: "#055a60",
            contrastText: '#eee',
            light: "#EBF2F2",
            lightGreen: "#A5D7DA"
        },
        secondary: {
            main: "#377B7F",
        },
        custom: {
            customGreen: "#7DBA61",
            customOrange: "#EE5A33",
            customBlue: "#029AB4"
        }
    }
})

// Embedded theme with different color scheme
const embeddedTheme = createTheme({
    palette: {
        primary: {
            main: "#1976d2",
            contrastText: '#fff',
            light: "#E3F2FD",
            lightGreen: "#90CAF9"
        },
        secondary: {
            main: "#42a5f5",
        },
        custom: {
            customGreen: "#66bb6a",
            customOrange: "#ff7043",
            customBlue: "#29b6f6"
        }
    }
})

const getRoutes = (globalSettings, canAccessApiHub) => {
    let site = [
        {
            type: "Home",
            roles: globalSettings.pages.find(page => page.type === "Home").roles,
            hasAccess: globalSettings.pages.find(page => page.type === "Home").roles.some(role => role.hasRole),
            path: "/",
            name: "Home",
            showInMenu: true,
            bannerSize: "Small",
            breadcrumb: [
                { path: "/", label: "Home" }
            ]
        },
        {
            type: "Admin",
            roles: globalSettings.pages.find(page => page.type === "Admin").roles,
            hasAccess: globalSettings.pages.find(page => page.type === "Admin").roles.some(role => role.hasRole),
            path: "/Admin",
            name: "Admin",
            showInMenu: false,
            bannerSize: "Small",
            breadcrumb: [
                { path: "/Admin", label: "Admin" }
            ]
        },
        {
            type: "Explore",
            roles: globalSettings.pages.find(page => page.type === "Explore").roles,
            hasAccess: globalSettings.pages.find(page => page.type === "Explore").roles.some(role => role.hasRole),
            path: "/Explore",
            name: "Explore",
            showInMenu: false,
            bannerSize: "Small",
            breadcrumb: [
                { path: "/Explore", label: "Explore" }
            ]
        },
        {
            type: "Bespoke",
            roles: globalSettings.pages.find(page => page.type === "Bespoke").roles,
            hasAccess: globalSettings.pages.find(page => page.type === "Bespoke").roles.some(role => role.hasRole),
            path: "/Bespoke",
            name: "Bespoke",
            showInMenu: false,
            bannerSize: "Small",
            breadcrumb: [
                { path: "/Bespoke", label: "Bespoke" }
            ]
        },
        {
            type: "Reports",
            roles: globalSettings.pages.find(page => page.type === "Reports").roles,
            hasAccess: globalSettings.pages.find(page => page.type === "Reports").roles.some(role => role.hasRole),
            path: "/Reports",
            name: "Reports",
            showInMenu: false,
            bannerSize: "Small",
            breadcrumb: [
                { path: "/Reports", label: "Reports" }
            ]
        },
        {
            type: "UserInteraction",
            roles: globalSettings.pages.find(page => page.type === "UserInteraction").roles,
            hasAccess: globalSettings.pages.find(page => page.type === "UserInteraction").roles.some(role => role.hasRole),
            path: "/UserInteraction",
            name: "UserInteraction",
            showInMenu: false,
            bannerSize: "Small",
            breadcrumb: [
                { path: "/UserInteraction", label: "UserInteraction" }
            ]
        },
        {
            type: "ManageLists",
            path: "/ManageLists",
            name: "Manage Lists",
            showInMenu: false,
            bannerSize: "Small",
            breadcrumb: [
                { path: "/ManageLists", label: "Manage Lists" }
            ]
        },
        {
            type: "ApiHub",
            roles: globalSettings.pages.find(page => page.type === "ApiHub")?.roles,
            hasAccess: globalSettings.pages.find(page => page.type === "Admin").roles.some(role => role.hasRole),//canAccessApiHub === true,
            path: "/apihub",
            name: "API Hub",
            showInMenu: false,
            bannerSize: "Small",
            breadcrumb: [
                { path: "/apihub", label: "API Hub" }
            ]
        },
        {
            type: "FinalMileAdmin",
            roles: globalSettings.pages.find(page => page.type === "FinalMileAdmin")?.roles,
            hasAccess: globalSettings.pages.find(page => page.type === "FinalMileAdmin")?.roles?.some(role => role.hasRole),
            path: "/finalmile-admin",
            name: "FinalMile Admin",
            showInMenu: false,
            bannerSize: "Small",
            breadcrumb: [
                { path: "/finalmile-admin", label: "FinalMile Admin" }
            ]
        },
    ]
    return site;
}


function App() {
    const isAuthenticated = useIsAuthenticated();
    const { instance, accounts, inProgress } = useMsal();
    const [graphData, setGraphData] = useState(null);
    const [idToken, setToken] = useState(null);
    const [roles, setRoles] = useState(null);
    const [globalSettings, setGlobalSettings] = useState(null);
    const [canAccessApiHub, setCanAccessApiHub] = useState(false);
    const [environment, setEnvironment] = useState('');
    const [errorMessage, setErrorMessage] = useState(null);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    let routes = globalSettings && !globalSettings.isError ? getRoutes(globalSettings.data, canAccessApiHub) : null;

    const [open, setOpen] = useState(true)
    const [drawerWidth, setDrawerWidth] = useState(220);
    const [isEmbedded, setIsEmbedded] = useState(false);
    const [parentTheme, setParentTheme] = useState(null);
    const interactiveRequestStarted = useRef(false);

    useEffect(() => {
        // Detect if page is embedded in an iframe
        const embedded = window.self !== window.top;
        setIsEmbedded(embedded);

        if (embedded) {
            // Request theme from parent window
            window.parent.postMessage({ type: 'REQUEST_THEME' }, '*');

            // Listen for theme data from parent
            const handleMessage = (event) => {
                if (event.data && event.data.type === 'PARENT_THEME') {
                    setParentTheme(event.data.theme);
                }
            };

            window.addEventListener('message', handleMessage);
            return () => window.removeEventListener('message', handleMessage);
        }
    }, []);

    // Create dynamic embedded theme based on parent styles
    const getActiveTheme = () => {

        if (parentTheme) {
            return createTheme({
                palette: {
                    primary: {
                        main: parentTheme.primaryColor || "#1976d2",
                        contrastText: parentTheme.textColor || '#fff',
                        light: parentTheme.lightColor || "#E3F2FD",
                        lightGreen: parentTheme.accentColor || "#90CAF9"
                    },
                    secondary: {
                        main: parentTheme.secondaryColor || "#42a5f5",
                    },
                    custom: {
                        customGreen: parentTheme.customGreen || "#66bb6a",
                        customOrange: parentTheme.customOrange || "#ff7043",
                        customBlue: parentTheme.customBlue || "#29b6f6"
                    },
                    background: {
                        default: parentTheme.backgroundColor || '#fff',
                        paper: parentTheme.paperColor || '#fff'
                    }
                }
            });
        }

        return standardTheme;
    };

    useEffect(() => {
        const hydrateAuthenticatedUser = async (response) => {
            setToken(response.idToken);
            setRoles(response.idTokenClaims.roles);
            getUser(response, setGraphData);

            try {
                const globalSettingsResponse = await fetchGlobalSettings(response.idToken, setGlobalSettings);
                if (globalSettingsResponse?.isError) {
                    setCanAccessApiHub(false);
                } else {
                    const accessResponse = await fetchFmApiHubAccess(response.idToken);
                    setCanAccessApiHub(!accessResponse.isError && accessResponse.data?.canAccess === true);
                }
            } catch {
                setCanAccessApiHub(false);
            }

            getEnvironment().then((response) => { setEnvironment(response) })
        };

        const bootstrapAuth = async () => {
            if (inProgress !== InteractionStatus.None) {
                return;
            }

            const request = await loginRequest();
            const account = accounts[0] || instance.getActiveAccount();

            if (!isAuthenticated || !account) {
                if (interactiveRequestStarted.current) {
                    return;
                }

                interactiveRequestStarted.current = true;
                await instance.loginRedirect(request);
                return;
            }

            try {
                const response = await instance.acquireTokenSilent({
                    ...request,
                    account
                });
                interactiveRequestStarted.current = false;
                await hydrateAuthenticatedUser(response);
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError) {
                    if (interactiveRequestStarted.current) {
                        return;
                    }

                    interactiveRequestStarted.current = true;
                    await instance.acquireTokenRedirect({
                        ...request,
                        account
                    });
                    return;
                }

                interactiveRequestStarted.current = false;
                throw error;
            }
        };

        bootstrapAuth();
    }, [accounts, inProgress, instance, isAuthenticated])

    const handleDrawerOpen = () => {
        setDrawerWidth(220);
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setDrawerWidth(50);
        setOpen(false);
    };


    useEffect(() => {
        if (globalSettings?.isError) {
            setErrorMessage("(Error: 400) There has been an error loading configuration. Please refresh the page (CTRL + F5). If the problem persists please contact support.");
            setShowErrorMessage(true);
        } else {
        }
    }, [globalSettings])

    return (
        <>
            <AuthenticatedTemplate>
                <ThemeProvider theme={getActiveTheme()}>
                    <BrowserRouter>
                        <NavigationBar user={graphData} isEmbedded={isEmbedded} />
                        <Toolbar />
                        {roles && (<NavigationDrawer drawerWidth={drawerWidth} handleDrawerOpen={handleDrawerOpen} handleDrawerClose={handleDrawerClose} open={open} routes={routes} isEmbedded={isEmbedded} />)}
                        <Container maxWidth={false}>
                            {idToken && (
                                <Routes>
                                    {roles ?
                                        <>
                                            <Route exact path="/" element={<HomePage idToken={idToken} drawerWidth={drawerWidth} environment={environment} isEmbedded={isEmbedded} />} />
                                            <Route exact path="/explore" element={
                                                <SecurityRoute routes={routes} path="Explore">
                                                    <ExplorePage />
                                                </SecurityRoute>}>
                                                <Route exact path="/explore" element={<DataProduct idToken={idToken} drawerWidth={drawerWidth} isEmbedded={isEmbedded} />} />
                                                <Route exact path="/explore/data-products/:dataProductName" element={<DataProductResult idToken={idToken} drawerWidth={drawerWidth} user={graphData} />} />
                                            </Route>
                                            <Route exact path="/bespoke" element={
                                                <SecurityRoute routes={routes} path="Bespoke">
                                                    <CustomViewPage idToken={idToken} drawerWidth={drawerWidth} user={graphData} />
                                                </SecurityRoute>
                                            } />
                                            <Route exact path="/reports" element={
                                                <SecurityRoute routes={routes} path="Reports">
                                                    <CustomReportsPage idToken={idToken} drawerWidth={drawerWidth} user={graphData} />
                                                </SecurityRoute>}>
                                            </Route>
                                            <Route exact path="/external-reports/:reportId" element={
                                                <ExternalReportsPage idToken={idToken} drawerWidth={drawerWidth} user={graphData} />
                                            } />
                                            <Route exact path="/external-reports" element={
                                                <ExternalReportsPage idToken={idToken} drawerWidth={drawerWidth} user={graphData} />
                                            } />
                                            <Route exact path="/userinteraction" element={
                                                <SecurityRoute routes={routes} path="UserInteraction">
                                                    <UserInteractionPage idToken={idToken} drawerWidth={drawerWidth} />
                                                </SecurityRoute>
                                            } />
                                            <Route exact path="/admin" element={
                                                <SecurityRoute routes={routes} path="Admin">
                                                    <AdminPage idToken={idToken} drawerWidth={drawerWidth} />
                                                </SecurityRoute>
                                            } />
                                            <Route exact path="/manageLists" element={
                                                    <ManageLists idToken={idToken} drawerWidth={drawerWidth} user={graphData} />
                                            } />
                                            <Route exact path="/manageLists/editList/:listId" element={<EditList idToken={idToken} drawerWidth={drawerWidth} user={graphData} />} />
                                            <Route exact path="/manageQueries" element={
                                                <ManageQueries idToken={idToken} drawerWidth={drawerWidth} user={graphData} />
                                            }>
                                            </Route>
                                            <Route exact path="/manageQueries/editQuery/:queryId" element={<EditQuery idToken={idToken} drawerWidth={drawerWidth} user={graphData} />} />
                                            <Route exact path="/snowflake-data-sources/:dataSourceId" element={
                                                <SnowflakeDataSourcesPage idToken={idToken} drawerWidth={drawerWidth} user={graphData} />
                                            } />
                                            <Route exact path="/snowflake-data-sources" element={
                                                <SnowflakeDataSourcesPage idToken={idToken} drawerWidth={drawerWidth} user={graphData} />
                                            } />
                                            <Route exact path="/apihub" element={
                                                <SecurityRoute routes={routes} path="ApiHub">
                                                    <ApiHubPage idToken={idToken} drawerWidth={drawerWidth} isEmbedded={isEmbedded} />
                                                </SecurityRoute>
                                            } />
                                            <Route exact path="/apihub/:productAlias" element={
                                                <SecurityRoute routes={routes} path="ApiHub">
                                                    <ApiHubProductPage idToken={idToken} drawerWidth={drawerWidth} />
                                                </SecurityRoute>
                                            } />
                                            <Route exact path="/finalmile-admin" element={
                                                <SecurityRoute routes={routes} path="FinalMileAdmin">
                                                    <FinalMileAdminPage idToken={idToken} drawerWidth={drawerWidth} />
                                                </SecurityRoute>
                                            } />
                                            <Route exact path="/finalmile-admin/:productId" element={
                                                <SecurityRoute routes={routes} path="FinalMileAdmin">
                                                    <FinalMileAdminProductPage idToken={idToken} drawerWidth={drawerWidth} />
                                                </SecurityRoute>
                                            } />
                                        </>
                                        :
                                        <Route exact path="*" element={<UnauthorizedPage environment={environment} />} />}
                                </Routes>
                            )}
                        </Container>
                    </BrowserRouter>
                </ThemeProvider>
            </AuthenticatedTemplate>
        </>
    );
}

export default App;
